import React, { useState } from "react";

// custom hooks
import useForm from "../../hooks/useForm";
import { useThunk } from "../../hooks/useThunk";

// thunks
import { submitContactForm } from "../../store";

// common constants
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";

// custom components
import CustomButton from "../Form/CustomButton";
import Input from "../Form/Input";
import Dropdown from "../Form/Dropdown";
import showToast from "../../utils/toastNotifications";

const Tooltip = ({ message }) => (
  <div
    className="
        absolute
        top-0
        left-1/2
        -translate-x-1/2
        -translate-y-full
        bg-gray-900
        text-white
        text-sm
        font-semibold
        rounded-md
        px-3
        py-1.5
        shadow-lg
        whitespace-nowrap
        select-none
        z-50
        pointer-events-none
        opacity-90
        transition-opacity
        duration-200
        "
    role="tooltip"
  >
    {message}
    <div
      className="
          absolute
          bottom-[-6px]
          left-1/2
          -translate-x-1/2
          w-3
          h-3
          bg-gray-900
          rotate-45
          "
    />
  </div>
);

const JoinWithUsForm = () => {
  const [submitContactFormThunk, isSubmitting] = useThunk(submitContactForm);

  const inputTypes = {
    name: { type: COMMON_FIELD_TYPES.FULLNAME },
    email: { type: COMMON_FIELD_TYPES.EMAIL_FIELD },
    phone: { type: COMMON_FIELD_TYPES.PHONE },
    roleType: {
      type: COMMON_FIELD_TYPES.DROPDOWN,
      options: [
        { _id: 1, label: "Care Assistant" },
        { _id: 2, label: "Support Worker" },
      ],
      displayKey: "label",
    },
    experience: { type: COMMON_FIELD_TYPES.TEXTAREA },
    acknowledgment: { type: COMMON_FIELD_TYPES.CHECKBOX },
  };

  const requiredFields = {
    name: true,
    email: true,
    phone: true,
    roleType: true,
    acknowledgment: true,
  };

  const [tooltipVisible, setTooltipVisible] = useState({
    name: false,
    email: false,
    phone: false,
    roleType: false,
    experience: false,
    acknowledgment: false,
    button: false,
  });

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
    clearError,
  } = useForm({}, requiredFields, inputTypes);

  // Helper: check if form is valid (no errors and all required fields filled)
  const isFormValid = () => {
    // All required fields must be filled
    const allFilled = Object.keys(requiredFields).every((key) => {
      if (typeof formData[key] === "boolean") return formData[key] === true;
      return formData[key] && String(formData[key]).trim() !== "";
    });
    // No errors
    const noErrors = Object.keys(requiredFields).every((key) => !errors[key]);
    return allFilled && noErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setTouched({
      email: true,
      phone: true,
      roleType: true,
      acknowledgment: true,
    });

    // Check acknowledgment before proceeding
    if (!formData.acknowledgment) {
      showToast("error", "Please confirm acknowledgment before submitting");
      return; // Stop form submission
    }

    if (validateForm()) {
      const response = await submitContactFormThunk(formData);

      if (response?.success) {
        showToast("success", "Form submitted successfully!");
      }
    }
  };

  return (
    <section className="lg:px-10 lg:mb-[100px] font-poppins">
      <h2 className="text-center text-[24px] lg:text-[32px] font-medium text-green lg:mt-[50px]  lg:mb-6 ">
        Join <span className="text-[var(--primary-orange-color)]">Us</span>
      </h2>
      <p className="text-[12px] lg:text-[18px] xl:text-[20px] text-[var(--light-gray-color)] text-center leading-relaxed mb-4 sm:mb-[50px]">
        Just fill out the form below and let us know a bit about yourself.
      </p>
      <div className="max-w-screen-xl mx-auto lg:shadow-[4px_4px_32px_var(--orange-shadow-color)] rounded-xl p-8 sm:p-12 font-poppins">
        <form onSubmit={handleSubmit} className="space-y-6 lg:mx-10">
          <div
            className="relative"
            onMouseEnter={() =>
              setTooltipVisible((prev) => ({ ...prev, name: true }))
            }
            onMouseLeave={() =>
              setTooltipVisible((prev) => ({ ...prev, name: false }))
            }
          >
            <Input
              name="name"
              label="Full Name"
              placeholder="Enter your Full Name"
              value={formData.name || ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
              onBlur={(e) => handleBlur("name", e.target.value)}
              isRequired={true}
              error={touched.name && errors.name ? errors.name : ""}
              inputStyle="rounded-xl"
            />
            {tooltipVisible.name && <Tooltip message="What's your name?" />}
          </div>

          <div
            className="relative"
            onMouseEnter={() =>
              setTooltipVisible((prev) => ({ ...prev, email: true }))
            }
            onMouseLeave={() =>
              setTooltipVisible((prev) => ({ ...prev, email: false }))
            }
          >
            <Input
              type="email"
              name="email"
              label="Email"
              placeholder="Enter your Email Address"
              value={formData.email}
              error={touched.email && errors.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              onBlur={(e) => handleBlur("email", e.target.value)}
              onFocus={() => clearError("email")}
              isRequired
              inputStyle="rounded-xl"
            />
            {tooltipVisible.email && (
              <Tooltip message="Your best email, so we can get in touch" />
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() =>
              setTooltipVisible((prev) => ({ ...prev, phone: true }))
            }
            onMouseLeave={() =>
              setTooltipVisible((prev) => ({ ...prev, phone: false }))
            }
          >
            <Input
              type="tel"
              name="phone"
              label="Phone Number"
              placeholder="Enter your Contact Number (9-15 digits)"
              value={formData.phone || ""}
              onChange={(e) => {
                // Only allow numeric input
                const numericValue = e.target.value.replace(/\D/g, "");
                handleInputChange("phone", numericValue);
              }}
              onBlur={(e) => handleBlur("phone", e.target.value)}
              isRequired
              error={errors.phone && touched.phone && errors.phone}
              inputStyle="rounded-xl"
              maxLength={15}
              minLength={9}
              pattern="[0-9]*"
              inputMode="numeric"
            />
            {tooltipVisible.phone && (
              <Tooltip message="Prefer a call? Add your number here" />
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() =>
              setTooltipVisible((prev) => ({ ...prev, roleType: true }))
            }
            onMouseLeave={() =>
              setTooltipVisible((prev) => ({ ...prev, roleType: false }))
            }
          >
            <Dropdown
              name="roleType"
              label="Type of Role Looking For"
              placeholder="Select Type of Role"
              isRequired
              options={inputTypes.roleType.options}
              displayKey="label"
              idKey="_id"
              defaultOption={
                inputTypes.roleType.options.find(
                  (opt) => opt.label === formData.roleType
                ) || null
              }
              onSelect={(val) =>
                handleInputChange("roleType", val?.label || "")
              }
              onBlur={handleBlur}
              error={errors.roleType && touched.roleType && errors.roleType}
              isForm={true}
              buttonClassName="rounded-xl"
            />
            {tooltipVisible.roleType && (
              <Tooltip message="Tell us what role you're interested in" />
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() =>
              setTooltipVisible((prev) => ({ ...prev, experience: true }))
            }
            onMouseLeave={() =>
              setTooltipVisible((prev) => ({ ...prev, experience: false }))
            }
          >
            <Input
              type="textarea"
              name="experience"
              label="Experience"
              placeholder="Enter your Message Here..."
              value={formData.experience || ""}
              onChange={(e) => handleInputChange("experience", e.target.value)}
              onBlur={(e) => handleBlur("experience", e.target.value)}
              error={
                errors.experience && touched.experience && errors.experience
              }
              inputStyle="rounded-xl h-32 lg:h-48"
            />
            {tooltipVisible.experience && (
              <Tooltip message="Feel free to share your background or story." />
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() =>
              setTooltipVisible((prev) => ({ ...prev, acknowledgment: true }))
            }
            onMouseLeave={() =>
              setTooltipVisible((prev) => ({ ...prev, acknowledgment: false }))
            }
          >
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                name="acknowledgment"
                id="acknowledgment"
                checked={formData.acknowledgment || false}
                onChange={(e) =>
                  handleInputChange("acknowledgment", e.target.checked)
                }
                onBlur={(e) => handleBlur("acknowledgment", e.target.checked)}
                className="mt-1 h-4 w-4 text-green focus:ring-green border-gray-300 rounded "
              />
              <label
                htmlFor="acknowledgment"
                className="text-sm lg:text-base text-gray-700 leading-relaxed cursor-pointer"
              >
                I acknowledge that I have provided personal and/or sensitive
                information willingly.
                <span className="text-red-500 ml-1">*</span>
              </label>
            </div>
            {errors.acknowledgment && touched.acknowledgment && (
              <p className="text-red-500 text-xs mt-1">
                {errors.acknowledgment}
              </p>
            )}
            {tooltipVisible.acknowledgment && (
              <Tooltip message="Please confirm you understand the information you're sharing" />
            )}
          </div>

          <div
            className="relative flex justify-center"
            onMouseEnter={() =>
              setTooltipVisible((prev) => ({ ...prev, button: true }))
            }
            onMouseLeave={() =>
              setTooltipVisible((prev) => ({ ...prev, button: false }))
            }
          >
            <CustomButton
              type="submit"
              bgColor="bg-green"
              buttonText={isSubmitting ? "Submitting..." : "Submit"}
              width="w-full lg:w-1/2"
              disabled={isSubmitting || !isFormValid()}
              loading={isSubmitting}
              onClick={handleSubmit}
            />
            {tooltipVisible.button && (
              <Tooltip message="Submit your details to us" />
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

export default JoinWithUsForm;
